import { defineStore } from "pinia";
import { Theme } from "@/models/Theme.model";
import { AgendaStore, AgendaStoreState } from "@/stores/AbstractAgenda.store";
import { orgApi } from "@/utils/Api.util";
import { useClassroomsStore } from "@/stores/Classrooms.store";
import { Interval } from "luxon";
import { ApiErrorMessageBag } from "@/stores/errors/ApiErrorMessageBag.ts";
import AlteredEntityHelper from "@/stores/AlteredEntity.helper";
import { AgendaFilterDef } from "@/stores/filters/AgendaFilterDef";


interface ThemeStoreState extends AgendaStoreState<Theme> {

}

const BaseStore = AgendaStore<Theme, ThemeStoreState>();

export const useThemesStore = defineStore('themes', {

	state: (): ThemeStoreState => ({
		errors: null,
		events: [],
		loadingRanges: 0,
		loadedRanges: [],
		lastFetchedRange: null
	}),

	getters: {

		...BaseStore.getters,

		apiPath(state: ThemeStoreState) {
			return "themes";
		},

		defaultMask(state: ThemeStoreState) {
			return [
				'*',
				'goals.*',
			];
		},

	},

	actions: {

		...BaseStore.actions,

		new(interval: Interval): Theme {

			this.clearErrors();

			const theme = new Theme();
			theme.dateRange = interval;

			return theme;

		},

		async findThemes(name: string): Promise<Theme[]> {

			const classroomId = useClassroomsStore().getCurrentClassroom().id;

			const response = await orgApi.get(
				'themes',
				{
					params: {
						query: name,
						classroomIds: classroomId,
					}
				}
			);

			return response.data.data.map((themeData: any) => {
				return Theme.mapFromServer(themeData);
			});
		},

		async store(theme: Theme) {

			this.clearErrors();
			if (theme.id) {
				return this.update(theme);
			}

			const serverData: any = theme.asApiObject();

			serverData.classroom = {
				id: useClassroomsStore().getCurrentClassroom().id
			};

			try {
				const response = await orgApi.post(this.apiPath, serverData, {
					params: {
						mask: this.defaultMask.join(',')
					}
				});
				theme.setFromServerData(response.data.data);

				this.events.push(theme);

				// Alas, we also need to reload all lessons, as the theme might have changed
				AlteredEntityHelper.processAlteredEntities(response.data.altered);

			} catch (e: any) {
				this.errors = ApiErrorMessageBag.fromAxiosException(e);
				throw this.errors;
			}

		},

		async update(theme: Theme) {

			this.clearErrors();
			const serverData: any = theme.asApiObject();

			try {
				const response = await orgApi.put(this.apiPath + '/' + theme.id, serverData, {
					params: {
						mask: this.defaultMask.join(',')
					}
				});

				// theme might be a clone; update the original
				this.getFromId(theme.id).setFromServerData(response.data.data);

				// Alas, we also need to reload all lessons, as the theme might have changed
				AlteredEntityHelper.processAlteredEntities(response.data.altered);

			} catch (e: any) {
				this.errors = ApiErrorMessageBag.fromAxiosException(e);
				throw this.errors;
			}

		},

		async delete(theme: Theme) {

			this.clearErrors();

			try {
				// Remove from the API
				await orgApi.delete("themes/" + theme.id);
				this.removeFromId(theme.id);
			} catch (e: any) {
				this.errors = ApiErrorMessageBag.fromAxiosException(e);
				throw this.errors;
			}

		},

		mapEventFromServer(event: any): Theme {
			return Theme.mapFromServer(event);
		}
	}

});
